import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ShoppingCart, FileText, Download } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useCart } from "@/contexts/CartContext";
import { Link, useNavigate } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { downloadSDS } from "@/utils/downloadUtils";
import { resolveProductImage, altTextFor } from "@/utils/productImages";
import { productUrl } from "@/utils/productUrl";

interface ProductCardProps {
  name: string;
  casNumber: string;
  category: string;
  description: string;
  availability: "In Stock" | "On Demand" | "Custom";
  certifications: string[];
  imageUrl?: string;
  price: {
    value: number;
    unit: string;
    currency: string;
    moq: number; // Minimum Order Quantity
  };
}

const ProductCard = ({
  name,
  casNumber,
  category,
  description,
  availability,
  certifications,
  imageUrl,
  price
}: ProductCardProps) => {
  const { toast } = useToast();
  const { addItem, openCart } = useCart();
  const navigate = useNavigate();
  const { t } = useLanguage();
  
  const getAvailabilityColor = (status: string) => {
    switch (status) {
      case "In Stock":
        return "bg-accent text-white";
      case "On Demand":
        return "bg-amber-500 text-white";
      default:
        return "bg-muted text-muted-foreground";
    }
  };

  // Pass casNumber as the stable id so two products in the same category
  // get visually different variants (same CAS → same variant on every
  // render — important for SEO / image cache stability).
  const resolvedImage = resolveProductImage(imageUrl, category, casNumber);
  const resolvedAlt = altTextFor(name, category, casNumber);

  const handleViewDetails = () => {
    navigate(productUrl({ name, casNumber }));
  };

  const handleDownloadSDS = () => {
    try {
      downloadSDS(casNumber, name);
      toast({
        title: t('product.download_started'),
        description: t('product.sds_downloading').replace('{product}', name),
      });
    } catch (error) {
      toast({
        title: t('product.download_error'),
        description: t('product.sds_error').replace('{product}', name),
        variant: "destructive"
      });
    }
  };

  const handleAddToCart = () => {
    // CAS number is unique per the products table — use it directly so two
    // products that happen to share digits (e.g. "134523-00-5" vs "13452-300-5")
    // don't collide in the cart.
    const cartItem = {
      id: casNumber,
      name,
      casNumber,
      category,
      description,
      availability,
      certifications,
      price
    };

    addItem(cartItem);
    
    toast({
      title: t('product.added_rfq'),
      description: t('product.added_cart').replace('{product}', name),
      action: (
        <Button 
          size="sm" 
          variant="outline"
          onClick={openCart}
        >
          {t('product.view_cart')}
        </Button>
      ),
    });
  };

  const detailHref = productUrl({ name, casNumber });

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden shadow-card hover:shadow-elevated transition-all duration-300 group">
      {/* Image + info are wrapped in a real <Link> so:
          - the whole card is clickable (image, title, CAS, description) —
            not just the small "Details" icon button at the bottom;
          - Googlebot follows an <a href> rather than a JS `navigate()`,
            which improves internal-link discovery for the 562 catalog URLs;
          - middle-click / cmd-click opens the detail page in a new tab,
            which is the behavior B2B buyers expect on a catalog listing.
          The action row (RFQ / SDS / details buttons) is kept OUTSIDE the
          Link so those buttons still fire their own handlers instead of
          navigating away when clicked. */}
      <Link
        to={detailHref}
        aria-label={`View ${name} product details`}
        className="block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
      >
        {/* Product Image */}
        <div className="aspect-video bg-muted relative overflow-hidden">
          <img
            src={resolvedImage}
            alt={resolvedAlt}
            title={resolvedAlt}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            width="400"
            height="300"
            loading="lazy"
            decoding="async"
          />
          <div className="absolute top-3 left-3">
            <Badge className={getAvailabilityColor(availability)}>
              {availability}
            </Badge>
          </div>
        </div>

        {/* Product Info */}
        <div className="p-5 sm:p-6 pb-0">
          <Badge variant="secondary" className="text-xs mb-2 inline-flex">
            {category}
          </Badge>
          <h3 className="font-semibold text-lg text-card-foreground mb-1 leading-tight group-hover:text-primary transition-colors line-clamp-2">
            {name}
          </h3>
          <p className="text-sm text-muted-foreground font-mono mb-3">
            {t('product.cas')}: {casNumber}
          </p>

          <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
            {description}
          </p>
        </div>
      </Link>

      {/* Action row — sits outside the <Link> so button handlers fire
          instead of the surrounding link's navigation. */}
      <div className="p-5 sm:p-6 pt-0">
        {/* Price Information. When no price is set on the row (price.value
            === 0) showing "USD 0/kg" makes the catalog look broken or free.
            Surface an RFQ prompt instead so the buyer understands the
            commercial flow. */}
        <div className="mb-4 p-3 bg-muted/30 rounded-lg">
          {price.value > 0 ? (
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="text-lg font-bold text-foreground whitespace-nowrap">
                  {price.currency} {price.value.toLocaleString()}
                  <span className="text-sm font-normal text-muted-foreground">/{price.unit}</span>
                </div>
                <div className="text-xs text-muted-foreground">
                  {t('product.moq')}: {price.moq.toLocaleString()} {price.unit}
                </div>
              </div>
              <Badge variant="outline" className="text-xs bg-accent/10 text-accent border-accent shrink-0">
                {t('product.bulk_discount')}
              </Badge>
            </div>
          ) : (
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="text-sm font-semibold text-foreground">
                  Quote on request
                </div>
                <div className="text-xs text-muted-foreground">
                  Bulk pricing · MOQ {price.moq.toLocaleString()} {price.unit}
                </div>
              </div>
              <Badge variant="outline" className="text-xs bg-accent/10 text-accent border-accent shrink-0">
                B2B
              </Badge>
            </div>
          )}
        </div>

        {/* Certifications */}
        {certifications.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-4">
            {certifications.slice(0, 3).map((cert, index) => (
              <Badge key={index} variant="outline" className="text-xs">
                {cert}
              </Badge>
            ))}
            {certifications.length > 3 && (
              <Badge variant="outline" className="text-xs">
                +{certifications.length - 3} more
              </Badge>
            )}
          </div>
        )}

        {/* Actions. Three buttons compete for ~280px on a 4-column grid at
            xl. We compress Details / SDS to icon-only at sm and reveal the
            label only when the card has room (md+). The primary RFQ action
            keeps its label everywhere but uses `min-w-0` so it can shrink
            instead of overflowing into the next column. */}
        <div className="flex items-center justify-between pt-4 border-t border-border gap-2">
          <div className="flex space-x-1 shrink-0">
            <Button
              variant="ghost"
              size="sm"
              className="text-muted-foreground hover:text-primary px-2"
              onClick={handleViewDetails}
              title={t('product.details')}
              aria-label={t('product.details')}
            >
              <FileText className="w-4 h-4" />
              <span className="ml-1 hidden xl:inline">{t('product.details')}</span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="text-muted-foreground hover:text-primary px-2"
              onClick={handleDownloadSDS}
              title={t('product.sds')}
              aria-label={t('product.sds')}
            >
              <Download className="w-4 h-4" />
              <span className="ml-1 hidden xl:inline">{t('product.sds')}</span>
            </Button>
          </div>

          <Button
            size="sm"
            variant="default"
            className="bg-primary hover:bg-primary-light min-w-0 truncate"
            onClick={handleAddToCart}
          >
            <ShoppingCart className="w-4 h-4 mr-1.5 shrink-0" />
            <span className="truncate">{t('product.add_rfq')}</span>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;